import React, { useCallback, useEffect, useMemo } from 'react'
import Card from 'components/Layout/Card'
import { useTranslation } from 'react-i18next'
import FillingSpinner from 'components/FillingSpinner'
import './InventoryCitiesOccupancy.scss'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../store'
import CardHeader from '../../../components/Layout/CardHeader'
import InventoryService from '../services/inventory.service'
import { citiesOccupancyActions } from '../store/inventory-cities-occupancy-slice'
import InventoryOccupancyChart from '../components/InventoryOccupancyChart'
import Pagination from '../../../components/Pagination'
import { DATE_PLACEHOLDER, ITEMS_PER_PAGE } from '../../../constant'
import DatePickerSingle from '../../../components/Form/DatePickerSingle'
import debounce from 'lodash/debounce'
import SelectMultiCheckbox from '../../../components/Form/SelectMultiCheckbox'
import { SelectOption } from '../../../components/Form/Select'
import Checkbox from '../../../components/Form/Checkbox'
import { InOut, MediaFormatCheckboxes } from '../InventoryChart/types'
import { MediaFormat } from 'types/campaign'
import MediaFormatUtils from 'utils/mediaFormat'
import InventoryDetailsModal from '../components/InventoryDetailsModal'

type IndoorMediaFormatKey = keyof MediaFormatCheckboxes['indoor']
type OutdoorMediaFormatKey = keyof MediaFormatCheckboxes['outdoor']
type MediaFormatKey = IndoorMediaFormatKey | OutdoorMediaFormatKey

const MEDIA_FORMAT_BY_KEY: Record<MediaFormatKey, MediaFormat> = {
  dgIn: MediaFormat.DG_IN,
  dgCl: MediaFormat.DG_CL,
  dgCs: MediaFormat.DG_CS,
}

const mediaFormatsToCheckboxes = (selected: MediaFormat[]): MediaFormatCheckboxes => ({
  indoor: {
    dgIn: selected.includes(MediaFormat.DG_IN),
  },
  outdoor: {
    dgCl: selected.includes(MediaFormat.DG_CL),
    dgCs: selected.includes(MediaFormat.DG_CS),
  },
})

const InventoryCitiesOccupancy: React.FC = () => {
  const debounceTime = 2000
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const debouncedFetchCitiesOccupancy = useCallback(
    debounce(InventoryService.fetchCitiesOccupancy, debounceTime),
    []
  )

  const {
    loading,
    filters,
    data,
    pageNumber,
    totalItemsCount,
    selectedCityData,
    selectedCityName,
  } = useSelector((state: RootState) => state.inventory.citiesOccupancy)

  const mediaCheckboxesState = useMemo(
    () => mediaFormatsToCheckboxes(filters.selected.mediaFormats),
    [filters.selected.mediaFormats]
  )

  const handleMediaFormatChange = <T extends InOut>(key: keyof MediaFormatCheckboxes[T]) => {
    void dispatch(citiesOccupancyActions.setSelectedMediaFormats([key as MediaFormat]))
  }

  const renderMediaFormatGroup = (group: InOut) => {
    const groupKeys = Object.keys(mediaCheckboxesState[group]) as Array<
      keyof MediaFormatCheckboxes[typeof group]
    >

    return (
      <div className='InventoryCitiesOccupancy__media-group'>
        <div className='InventoryCitiesOccupancy__media-group-title'>{t(`common.${group}`)}</div>
        <div className='InventoryCitiesOccupancy__media-group-checkboxes'>
          {groupKeys.map(key => {
            const typedKey = key as MediaFormatKey
            return (
              <Checkbox
                key={typedKey}
                className='InventoryCitiesOccupancy__media-checkbox'
                id={`${group}-${typedKey}`}
                checked={mediaCheckboxesState[group][key]}
                onChange={() => {
                  dispatch(citiesOccupancyActions.setLoading(true))
                  handleMediaFormatChange(key as keyof MediaFormatCheckboxes[typeof group])
                }}
              >
                <img
                  alt={typedKey}
                  src={MediaFormatUtils.getIcon(MEDIA_FORMAT_BY_KEY[typedKey])}
                />
                {t(`mediaType.${typedKey}`)}
              </Checkbox>
            )
          })}
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (filters.selected.mediaFormats.length) {
      void debouncedFetchCitiesOccupancy(
        filters.selected.fromDate,
        filters.selected.toDate,
        filters.selected.agglomerations,
        filters.selected.cities,
        filters.selected.mediaFormats,
        1
      )
    }
  }, [filters.selected])

  useEffect(() => {
    const payload = {}

    void InventoryService.fetchInventoryFilters(payload)
  }, [])

  useEffect(() => {
    return () => {
      dispatch(citiesOccupancyActions.resetState())
    }
  }, [])

  const setPageNumber = (page: number) => {
    void InventoryService.fetchCitiesOccupancy(
      filters.selected.fromDate,
      filters.selected.toDate,
      filters.selected.agglomerations,
      filters.selected.cities,
      filters.selected.mediaFormats,
      page
    )
  }

  const selectInventoryCity = (cityName: string) => {
    if (!filters.selected.mediaFormats.length) {
      return
    }

    void InventoryService.fetchSingleCityOccupancyDetails(
      filters.selected.fromDate,
      filters.selected.toDate,
      filters.selected.agglomerations,
      cityName,
      filters.selected.mediaFormats[0]
    )
  }

  const closeInventoryCityModal = () => {
    void dispatch(
      citiesOccupancyActions.setSelectedCityDetails({
        cityName: '',
        data: [],
      })
    )
  }

  const stringToSelectOption = (options: string[]): SelectOption[] => {
    return options
      .map(option => ({ value: option, label: option }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }

  return (
    <div className='InventoryCitiesOccupancy'>
      <Card>
        <CardHeader>
          {t('inventory.citiesOccupancy.header') + ' ' + filters.selected.mediaFormats.join(',')}
        </CardHeader>
        <div>
          <div className='InventoryCitiesOccupancy__media-section'>
            <div className='InventoryCitiesOccupancy__media-section-title'>
              {t('common.mediaType')}
            </div>
            <div className='InventoryCitiesOccupancy__media-section-groups'>
              {renderMediaFormatGroup('indoor')}
              {renderMediaFormatGroup('outdoor')}
            </div>
          </div>
          {filters.selected.mediaFormats.length > 0 && (
            <div className='InventoryCitiesOccupancy__filters'>
              <DatePickerSingle
                id='dateFromCities'
                value={filters.selected.fromDate}
                placeholder={DATE_PLACEHOLDER}
                title={t('common.from')}
                onChange={(fromDate): void => {
                  void dispatch(
                    citiesOccupancyActions.setSelectedFilterDate({
                      key: 'fromDate',
                      data: fromDate,
                    })
                  )
                }}
              />
              <DatePickerSingle
                id='dateToCities'
                value={filters.selected.toDate}
                placeholder={DATE_PLACEHOLDER}
                title={t('common.to')}
                onChange={(toDate): void => {
                  void dispatch(
                    citiesOccupancyActions.setSelectedFilterDate({
                      key: 'toDate',
                      data: toDate,
                    })
                  )
                }}
              />
              {filters.selected.mediaFormats[0] !== MediaFormat.DG_CS && (
                <SelectMultiCheckbox
                  id='agglomerationsCities'
                  title={t('common.agglomeration')}
                  value={filters.selected.agglomerations}
                  options={stringToSelectOption(filters.all.agglomerations)}
                  onChange={selected => {
                    const payload = {
                      agglomerations: selected,
                      cities: filters.selected.cities,
                    }

                    void InventoryService.fetchInventoryFilters(payload)

                    void dispatch(
                      citiesOccupancyActions.setSelectedFilterDropdown({
                        key: 'agglomerations',
                        data: selected,
                      })
                    )
                  }}
                  selectAllOption
                />
              )}
            </div>
          )}

          {filters.selected.mediaFormats.length > 0 && data && data.length > 0 && !loading && (
            <>
              <div className='InventoryCitiesOccupancy__charts'>
                {data.map((item, index) => {
                  return (
                    <InventoryOccupancyChart
                      key={index}
                      inventoryItem={item}
                      selectInventoryItem={selectInventoryCity}
                    />
                  )
                })}
              </div>
              <div className='InventoryCitiesOccupancy__pagination'>
                <Pagination
                  activePage={pageNumber}
                  itemsCountPerPage={ITEMS_PER_PAGE}
                  totalItemsCount={totalItemsCount}
                  onChange={setPageNumber}
                />
              </div>
            </>
          )}
          {loading && (
            <div className={'InventoryCitiesOccupancy__spinner-container'}>
              <FillingSpinner className='InventoryCitiesOccupancy__loading' />
            </div>
          )}
        </div>
        <InventoryDetailsModal
          inventoryData={selectedCityData}
          closeClick={closeInventoryCityModal}
          title={selectedCityName}
        />
      </Card>
    </div>
  )
}

export default InventoryCitiesOccupancy
